import { Check, Clock, CreditCard, Lock, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import * as React from 'react'

import { cn } from '../lib/utils'
import { Button } from './button'
import { PillButton } from './pill-button'
import { Tag } from './tag'

/**
 * The app-wide **billing-jeopardy surface**: payment failures and trial endings, wherever the user
 * happens to be. `none` renders nothing.
 *
 * ## Eight states, four severities
 *
 * The states are Stripe's own vocabulary (`past_due`, `paused`), so the app never translates
 * between its billing model and this component's. Severity is the visual ramp underneath, and the
 * two are mapped in {@link BILLING_BANNER_STATES}. Adding a ninth state later means picking a
 * severity, not designing a banner.
 *
 * `paused` shares `urgent` with the last day of grace because it is functionally the same moment —
 * access is about to stop and one action fixes it. Only the words differ.
 *
 * ## Dismissibility is derived, never passed
 *
 * A caller cannot make `past_due` dismissible: the state table owns it. Involuntary churn from
 * expired cards is typically the largest single source of subscription loss, so "the user can
 * dismiss the only warning they'll get" is the failure this shape rules out structurally.
 *
 * Trial-ending states *are* dismissible — including `trial_t0`, which steps up to amber because
 * the day matters but keeps its dismiss because access is still live that morning. Taking it away
 * on the last day punishes someone who hasn't done anything wrong yet.
 *
 * ## It does not own the top of the viewport
 *
 * No `fixed`, no `sticky`, no `z-index`. A flow element that animates its own height; insets come
 * from `className`. Stacking against `ConnectionBanner` is the app's job — see the priority order
 * in the wiring story, not this component.
 */
export type BillingBannerState =
  | 'none'
  | 'past_due'
  | 'grace_today'
  | 'locked'
  | 'paused'
  | 'trial_t7'
  | 'trial_t3'
  | 'trial_t0'
  | 'recovered'

type Severity = 'info' | 'notice' | 'urgent' | 'locked' | 'success'

/**
 * State → severity, icon, and dismissibility. Exported so the app can ask whether a state is
 * dismissible (to decide whether to persist a dismissal) without duplicating the table.
 */
export const BILLING_BANNER_STATES: Record<
  Exclude<BillingBannerState, 'none'>,
  { severity: Severity; Icon: typeof Clock; dismissible: boolean }
> = {
  past_due: { severity: 'notice', Icon: CreditCard, dismissible: false },
  grace_today: { severity: 'urgent', Icon: CreditCard, dismissible: false },
  locked: { severity: 'locked', Icon: Lock, dismissible: false },
  paused: { severity: 'urgent', Icon: Clock, dismissible: false },
  trial_t7: { severity: 'info', Icon: Clock, dismissible: true },
  trial_t3: { severity: 'info', Icon: Clock, dismissible: true },
  trial_t0: { severity: 'notice', Icon: Clock, dismissible: true },
  recovered: { severity: 'success', Icon: Check, dismissible: false }
}

/**
 * Every surface here is DS-native — this component introduces no new color.
 *
 * `notice` → `urgent` escalates through the **action**, not the surface: same amber wash, quiet
 * pill becomes a solid one. Deepening the wash instead would mean re-tuning ink contrast in both
 * themes for no extra signal, and `amber-soft`/`amber-strong` invert between light and dark, so a
 * saturation ramp built on them doesn't ramp in dark at all.
 *
 * `locked` is the one hue change and it is deliberate. After thirteen days of amber, more amber
 * says nothing — access is already gone, which is a different category. Deep green rather than red
 * because red stays reserved for destructive *user actions*: a red "Update card" would tell the
 * user that the safe, desirable action is the dangerous one.
 *
 * ### The amber ink diverges from `ConnectionBanner` on purpose
 *
 * `ConnectionBanner` and `InviteAllowance` both put `text-amber-strong` on `bg-amber-wash`, which
 * fails WCAG AA in light mode — `--amber-strong` resolves to a *fill* tone, not an ink tone. This
 * uses `--amber-800` in light and keeps `--amber-strong` in dark (where it inverts to a light step
 * and already passes). See #1214, which tracks the design-system fix across every affected
 * component; when it lands an ink role, this switches to it and the divergence disappears.
 */
const TONE: Record<Severity, { surface: string; title: string; body: string; icon: string }> = {
  info: {
    surface: 'bg-card border-border',
    title: 'text-foreground',
    body: 'text-muted-foreground',
    icon: 'text-muted-foreground'
  },
  notice: {
    surface: 'bg-amber-wash border-amber-hairline',
    title: 'text-amber-800 dark:text-amber-strong',
    body: 'text-amber-800/[0.82] dark:text-amber-strong/[0.82]',
    icon: 'text-amber-800 dark:text-amber-strong'
  },
  urgent: {
    surface: 'bg-amber-wash border-amber-hairline',
    title: 'text-amber-800 dark:text-amber-strong',
    body: 'text-amber-800/[0.82] dark:text-amber-strong/[0.82]',
    icon: 'text-amber-800 dark:text-amber-strong'
  },
  locked: {
    // Deep green needs separation from the near-black dark `--background`; transparent in light,
    // where the surface is already the highest-contrast thing on the page.
    surface: 'bg-brand-green-deep border-transparent dark:border-brand-green-700',
    title: 'text-brand-green-on-deep',
    body: 'text-brand-green-on-deep/[0.78]',
    icon: 'text-brand-green-on-deep/[0.85]'
  },
  success: {
    surface: 'bg-brand-green-50 border-brand-green-200 dark:bg-brand-green-900/60 dark:border-brand-green-800',
    title: 'text-brand-green-700 dark:text-brand-green-300',
    body: 'text-brand-green-700/[0.82] dark:text-brand-green-300/[0.82]',
    icon: 'text-brand-green-700 dark:text-brand-green-300'
  }
}

/** Resolved copy. `@parsleyai/ui` is i18n-free — the app passes already-translated strings. */
export interface BillingBannerLabels {
  /** e.g. "We couldn't charge your card" */
  title: string
  /** Optional second line. e.g. "Update it to keep your plans and recipes." */
  body?: string
  /**
   * Pre-formatted count, e.g. "13 days left" / "Last day" / "Today". Rendered as a mono `Tag`.
   * The component does no date math and imports no date library.
   */
  countdown?: string
  /** `aria-label` for the dismiss control. Defaults to "Dismiss". */
  dismiss?: string
}

export interface BillingBannerProps {
  /** Current billing state. `none` renders nothing. */
  state: BillingBannerState
  /** Resolved copy (app-translated). */
  labels: BillingBannerLabels
  /** The single action. Its destination is an app concern. */
  action?: { label: string; onClick: () => void }
  /** Called only for dismissible states; the component ignores it otherwise. */
  onDismiss?: () => void
  /**
   * `stacked` is the mobile card: title, body and action down the page. `row` puts the same
   * content on one line for desktop, so a paragraph-width message doesn't push every tile down on
   * a 1440 screen. Same card surface either way — one prop rather than a second component, because
   * the content contract is identical.
   */
  layout?: 'stacked' | 'row'
  /**
   * Applied to the outer `role="status"` region. This is where the caller sets width: `row`
   * deliberately fills whatever it's given, so pass the app's content column (e.g.
   * `mx-auto w-full max-w-page`) to keep the banner aligned with the page beneath it.
   */
  className?: string
}

// Matches ConnectionBanner exactly, so the two banners animate identically in the shared slot.
const INSET_EASE: [number, number, number, number] = [0.22, 0.68, 0, 1]

/** The height animation — the page making room. Unchanged from `ConnectionBanner`. */
const EXPAND = { duration: 0.36, ease: INSET_EASE }

/**
 * The content fades in *behind* the expansion rather than with it: the row reads as the page
 * opening a gap and the message arriving into it, instead of a fully-formed block materialising
 * while the layout is still moving.
 *
 * **`FADE_IN_DELAY` is the knob.** Measured in Chrome at 0.16 (rAF probe against the `Dismissible`
 * story, 96 frames):
 *
 * | | |
 * |---|---|
 * | height 50% / 99% | 62ms / **287ms** |
 * | opacity leaves 0 | 170ms |
 * | opacity 99% | **429ms** |
 *
 * So the fade lands **142ms after** the box stops moving — not the ~80ms the nominal durations
 * suggest, because `INSET_EASE` front-loads the height and it visually settles at 287ms rather
 * than its nominal 360ms. Raise the delay for a more deliberate arrival; drop it toward 0.08 to
 * close that gap to roughly 80ms, or to 0 to fade and expand together.
 */
const FADE_IN_DELAY = 0.16
const FADE_IN = { duration: 0.28, delay: FADE_IN_DELAY }

/** Leaving isn't a moment worth staging — collapse and fade together, no delay. */
const FADE_OUT = { duration: 0.3 }

const INSTANT = { duration: 0.001 }

export const BillingBanner = React.forwardRef<HTMLDivElement, BillingBannerProps>(
  ({ state, labels, action, onDismiss, layout = 'stacked', className }, ref) => {
    const reduced = useReducedMotion()
    const config = state === 'none' ? null : BILLING_BANNER_STATES[state]
    const tone = TONE[config?.severity ?? 'info']
    const row = layout === 'row'

    const icon = config ? <config.Icon className={cn('h-[17px] w-[17px] shrink-0', tone.icon)} strokeWidth={1.8} /> : null

    const countdown = labels.countdown && config && config.severity !== 'locked' && (
      // `neutral` on the card surface, amber on the washes.
      //
      // The amber tone needs the same light-mode ink override as the banner body. `Tag`'s amber ink
      // is `color-mix(--primary 82%, #000)`, which looks like it should be dark enough and isn't:
      // measured in-browser on the composited stack it lands at **3.23:1** at 10px/700 — small text,
      // so AA wants 4.5:1. `text-amber-800` takes it to 7.6:1. Dark mode already passes (6.71:1) and
      // is left alone. Same root cause as the body ink, same #1214 fix will retire both.
      <Tag
        tone={config.severity === 'info' ? 'neutral' : 'amber'}
        className={cn('shrink-0 font-mono', config.severity !== 'info' && 'text-amber-800 dark:text-amber-strong')}>
        {labels.countdown}
      </Tag>
    )

    // The action escalates with severity: quiet pill → solid → the on-green primary on the inverse
    // surface.
    //
    // Size is the DS default (36px), not `lg` (44px), and that is a deliberate exception to the
    // 44×44 touch-target rule in CLAUDE.md. The handoff specified 44px and it was built that way
    // first — but measured in the browser it renders 37% taller than `ConnectionBanner`'s action
    // (32px) sitting in the same slot, and taller than every other pill in the app (36px). A banner
    // action that doesn't match any other button in the product is a worse outcome than 8px of
    // target. It still clears WCAG 2.5.8's 24px minimum. If the 44px target is ever enforced
    // properly it should move for `ConnectionBanner` and the DS default at the same time, not here
    // alone. (The dismiss control below keeps its full 44×44 — it has no visible box, so a larger
    // hit area costs nothing visually.)
    //
    // `urgent` is the design's solid amber, via `Button variant="default"` — `--primary` is OKLCH
    // hue 72.8, i.e. amber, not green. An earlier pass substituted a green pill here on the grounds
    // that the DS had no amber action at 44px; that reasoning died with the 44px decision above,
    // since Button's default size is h-9, the same 36px as the pills beside it.
    const actionEl =
      config &&
      action &&
      (config.severity === 'urgent' ? (
        <Button variant="default" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : (
        <PillButton variant={config.severity === 'locked' ? 'on-green-primary' : 'secondary'} onClick={action.onClick}>
          {action.label}
        </PillButton>
      ))

    const dismissEl = config?.dismissible && (
      <button
        type="button"
        onClick={onDismiss}
        aria-label={labels.dismiss ?? 'Dismiss'}
        // Full 44×44 hit area, pulled back with negative margin so it doesn't inflate the card.
        className={cn(
          '-mr-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors',
          'hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-pelorous-300',
          row ? '' : '-mt-1.5',
          tone.icon
        )}>
        <X className="h-4 w-4" />
      </button>
    )

    return (
      // biome-ignore lint/a11y/useSemanticElements: a polite status live-region hosting animated banner content + page insets; <output> is a form-result element with the wrong semantics. Deliberately `polite`, not `alert` — this is a standing condition, not an event, and stealing focus on every route change would be hostile.
      <div ref={ref} role="status" aria-live="polite" className={className}>
        <AnimatePresence initial={false}>
          {config && (
            <motion.div
              key="billing-banner"
              className="w-full overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              // Exit carries its own transition so the staged fade applies to arriving only — a
              // delay on the way out would leave the text hanging in a box that's already closing.
              exit={{ height: 0, opacity: 0, transition: reduced ? INSTANT : { height: EXPAND, opacity: FADE_OUT } }}
              transition={reduced ? INSTANT : { height: EXPAND, opacity: FADE_IN }}>
              {row ? (
                // Same card chrome as `stacked` — the difference is the arrangement, not the
                // surface. One row keeps a paragraph-width message from pushing every tile down on
                // a 1440 screen, which is the whole reason this layout exists.
                //
                // It is NOT full-bleed, and an earlier version was. A bleeding band has to escape
                // the page's horizontal padding, which means mounting above `main` — and `main`'s
                // top padding is the only thing holding content clear of the fixed mobile top bar,
                // so anything above it disappears underneath. Width is the caller's to set; give it
                // the app's content column so it lines up with what sits beneath it.
                <div className="pt-2 pb-5">
                  <div className={cn('flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-card', tone.surface)}>
                    {icon}
                    {/* Title and body stack; the controls stay on one line beside them.

                        The body was inline after the title and `truncate`d, which is how a banner
                        quietly deletes the half of the sentence that mattered — `locked`'s "Nothing
                        has been deleted." is the reassurance, and it was the part being cut. Copy
                        length varies by state and will change again, so the layout has to absorb it
                        rather than every string being tuned to fit. Two text lines still beat the
                        stacked card, which puts the action on a third. */}
                    <div className="min-w-0 flex-1">
                      <div className={cn('text-sm font-semibold', tone.title)}>{labels.title}</div>
                      {labels.body && <div className={cn('mt-0.5 text-sm leading-snug', tone.body)}>{labels.body}</div>}
                    </div>
                    {countdown}
                    {actionEl}
                    {dismissEl}
                  </div>
                </div>
              ) : (
                // Padding inside the overflow-hidden clip so `shadow-card` isn't cropped.
                <div className="flex justify-center px-3 pt-2 pb-5">
                  <div className={cn('w-full max-w-md rounded-2xl border shadow-card', tone.surface)}>
                    <div className="flex items-start gap-3 p-3.5">
                      <span className="mt-0.5 flex">{icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <div className={cn('flex-1 text-sm font-semibold leading-snug', tone.title)}>{labels.title}</div>
                          {countdown}
                        </div>
                        {labels.body && <div className={cn('mt-1 text-xs leading-snug', tone.body)}>{labels.body}</div>}
                        {actionEl && <div className="mt-3 flex">{actionEl}</div>}
                      </div>
                      {dismissEl}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }
)
BillingBanner.displayName = 'BillingBanner'
